import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Card,
  Button,
  Box,
} from "@material-ui/core";
import CloseIcon from "../../assets/icon/CloseIcon";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import {
  buildBusinessConnectPayload,
  launchWhatsAppCloudSignup,
} from "../../utils/whatsappCloudSignup";
import { launchInstagramPageOAuth } from "../../utils/instagramPageOAuth";
import { launchMessengerPageOAuth } from "../../utils/messengerPageOAuth";

import messengerIcon from "../../assets/img/messenger.png";
import whatsappIcon from "../../assets/img/whatsapp.png";
import instagramIcon from "../../assets/img/instagram.png";
import smsIcon from "../../assets/img/chat.png";

const useStyles = makeStyles((theme) => ({
  modalContainer: {
    background: theme.palette.tabHeaderBackground,
    overflow: "hidden",
  },
  dialogContent: {
    padding: theme.spacing(2),
    overflow: "hidden",
  },
  scrollContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
    scrollBehavior: "smooth",
    maxHeight: "60vh",
    overflowY: "auto",
    overflowX: "hidden",
    [theme.breakpoints.up("md")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
      maxHeight: "70vh",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      maxHeight: "50vh",
    },
    "&::-webkit-scrollbar": {
      width: 8,
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.background.default,
      borderRadius: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.primary.main,
      borderRadius: 4,
      "&:hover": {
        background: theme.palette.primary.dark,
      },
    },
  },
  cardWrapper: {
    width: "100%",
    height: "200px",
    minWidth: "280px",
    minHeight: "180px",
    transition: "all 0.3s ease",
    [theme.breakpoints.up("md")]: {
      height: "220px",
    },
    [theme.breakpoints.down("sm")]: {
      height: "160px",
      minWidth: "250px",
      minHeight: "140px",
    },
  },
  channelCard: {
    cursor: "pointer",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    transition: "all 0.3s",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: theme.shadows[4],
    },
  },
  channelIcon: {
    width: 48,
    height: 48,
    marginBottom: theme.spacing(1),
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "contain",
    },
  },
  channelName: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    fontSize: "1rem",
  },
  channelDescription: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    marginBottom: 10,
    fontSize: "0.875rem",
    lineHeight: "1.2",
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
    color: "#ff1744",
  },
  comingSoon: {
    position: "absolute",
    top: theme.spacing(2),
    right: theme.spacing(2),
    background: theme.palette.primary.main,
    color: "white",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: "0.75rem",
  },
}));

const SelectChannelModal = ({ open, onClose, onSelect }) => {
  const classes = useStyles();
  const [isConnecting, setIsConnecting] = useState(false);

  const channels = [
    {
      id: "whatsapp",
      name: "WhatsApp",
      description:
        "Conecta WhatsApp Business para habilitar la interacción y soporte fluido con el cliente.",
      icon: whatsappIcon,
      aviable: true,
    },
    {
      id: "whatsapp_api",
      name: "WhatsApp API",
      description:
        "Conecta WhatsApp Cloud vía Meta Embedded Signup (token + WABA). No usa código QR.",
      icon: whatsappIcon,
      aviable: true,
    },
    {
      id: "webchat",
      name: "Web Chat",
      description:
        "Integra un widget de chat en tu sitio web para que tus clientes puedan comunicarse directamente contigo desde tu página.",
      icon: smsIcon,
      aviable: true,
    },
    {
      id: "messenger",
      name: "Facebook Messenger",
      description:
        "Conecta tu Facebook Page vía Meta OAuth. Recibe y responde mensajes de Messenger.",
      icon: messengerIcon,
      aviable: true,
    },
    {
      id: "instagram",
      name: "Instagram",
      description:
        "Conecta Instagram para responder mensajes privados y construir conexiones sólidas con tu marca.",
      icon: instagramIcon,
      aviable: true,
    },
  ];

  const handleChannelSelect = (channel) => {
    if (!channel.comingSoon) {
      onSelect(channel.id);
    }
  };

  const handleInstagramConnect = async (event) => {
    event.stopPropagation();
    if (isConnecting) {
      return;
    }

    setIsConnecting(true);
    try {
      const signup = await launchInstagramPageOAuth();

      if (signup.connections?.length) {
        toast.success("Instagram conectado correctamente");
        if (onSelect) {
          onSelect("instagram", {
            type: "official_connection",
            data: { connections: signup.connections, count: signup.count },
          });
        }
        setTimeout(onClose, 800);
      }
    } catch (error) {
      if (error.message !== "signup_canceled") {
        toastError(error);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const handleMessengerConnect = async (event) => {
    event.stopPropagation();
    if (isConnecting) {
      return;
    }

    setIsConnecting(true);
    try {
      const signup = await launchMessengerPageOAuth();

      if (signup.connections?.length) {
        toast.success("Messenger conectado correctamente");
        if (onSelect) {
          onSelect("messenger", {
            type: "official_connection",
            data: { connections: signup.connections, count: signup.count },
          });
        }
        setTimeout(onClose, 800);
      }
    } catch (error) {
      if (error.message !== "signup_canceled") {
        toastError(error);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const handleCloudSignup = async (event) => {
    event.stopPropagation();
    if (isConnecting) {
      return;
    }

    setIsConnecting(true);
    try {
      const signup = await launchWhatsAppCloudSignup();

      if (signup.code) {
        const response = await api.post(
          "/whatsapp/business",
          buildBusinessConnectPayload(signup),
          { timeout: 30000 },
        );
        if (response.data?.success) {
          toast.success("WhatsApp Cloud conectado correctamente");
          if (onSelect) {
            onSelect("whatsapp_api", {
              type: "official_connection",
              data: response.data,
            });
          }
          setTimeout(onClose, 800);
        }
        return;
      }

      if (signup.connections?.length) {
        toast.success("WhatsApp Cloud conectado correctamente");
        if (onSelect) {
          onSelect("whatsapp_api", {
            type: "official_connection",
            data: signup,
          });
        }
        setTimeout(onClose, 800);
      }
    } catch (error) {
      if (error.message !== "signup_canceled") {
        toastError(error);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.modalContainer}>
        {i18n.t("selectChannel.title")}
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={`${classes.modalContainer} ${classes.dialogContent}`}>
        <div className={classes.scrollContainer}>
          {channels.map((channel) => (
            <div key={channel.id} className={classes.cardWrapper}>
              <Card
                className={classes.channelCard}
                onClick={() => handleChannelSelect(channel)}
              >
                <div className={classes.channelIcon}>
                  <img src={channel.icon} alt={channel.name} />
                </div>
                <Typography variant="h6" className={classes.channelName}>
                  {channel.name}
                </Typography>
                <Typography variant="body2" className={classes.channelDescription}>
                  {channel.description}
                </Typography>
                {channel.id === "whatsapp" ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChannelSelect(channel);
                    }}
                  >
                    Conectar
                  </Button>
                ) : channel.id === "whatsapp_api" ? (
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={handleCloudSignup}
                      disabled={isConnecting}
                    >
                      {isConnecting ? "Conectando..." : "Conectar con Meta"}
                    </Button>
                  </Box>
                ) : channel.id === "messenger" ? (
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={handleMessengerConnect}
                      disabled={isConnecting}
                    >
                      {isConnecting ? "Conectando..." : "Conectar Page"}
                    </Button>
                  </Box>
                ) : channel.id === "instagram" ? (
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={handleInstagramConnect}
                      disabled={isConnecting}
                    >
                      {isConnecting ? "Conectando..." : "Conectar Instagram"}
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant={channel.comingSoon ? "outlined" : "contained"}
                    color="primary"
                    disabled={channel.comingSoon}
                    size="small"
                  >
                    {channel.comingSoon ? "Próximamente" : "Conectar"}
                  </Button>
                )}
              </Card>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectChannelModal;
